import { ExternalLink } from "ui/Link";
import { cn } from "@/lib/utils";
import { COLLECT_MODE_LABELS } from "pages/collect/components/collect-mode-pill";
import type { CollectionFile } from "shared/types/files";

import {
  contentCollectSubmitter,
  formatCollectDate,
  formatCollectDateTime,
  getContentCollectMeta,
  humanizePermittedUse,
  rightsHaveExpired,
} from "./content-collect-provenance";

/**
 * "Where did this come from, and what did they agree to?" — answered in the
 * asset detail modal, months after the fact, without asking anyone
 * (CC-31 / SKE-4288).
 *
 * CONSENT IS A RECORD, NOT A CONTROL (D4). An expired rights window gets its
 * own colour so it can't be missed, but the copy stays informational: Sked
 * does not block, gate, or restrict a collected asset, and this panel must
 * never suggest it does.
 *
 * Renders nothing for an asset with no `meta.contentCollect`, so the modal can
 * mount it unconditionally.
 */

const LABEL_CLASSES = "font-gotham text-sm font-semibold text-neutral-700 w-24";
const VALUE_CLASSES = "font-inter text-sm text-neutral-600";

function ProvenanceRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-2">
      <span className={cn(LABEL_CLASSES, "shrink-0")}>{label}</span>
      <span className={cn(VALUE_CLASSES, "min-w-0 flex-1")}>{children}</span>
    </div>
  );
}

export function ContentCollectProvenancePanel({
  asset,
  className,
  showSourceNotes = true,
}: {
  asset: CollectionFile;
  className?: string;
  /**
   * The detail modal already renders `source.notes` in its own field, but only
   * when the asset carries dimensions and a size. The caller passes `false`
   * there so the sentence appears exactly once on screen.
   */
  showSourceNotes?: boolean;
}) {
  const meta = getContentCollectMeta(asset);
  if (!meta) return null;

  const submitter = contentCollectSubmitter(meta);
  const submittedAt = formatCollectDateTime(meta.submittedAt);
  const modeLabel = meta.mode ? COLLECT_MODE_LABELS[meta.mode] : null;
  const promptTitle = meta.prompt?.title?.trim();
  const consent = meta.consent;
  const permittedUses = (consent?.permittedUses ?? []).filter(Boolean);
  const rightsExpiresAt = formatCollectDate(consent?.rightsExpiresAt);
  const expired = rightsHaveExpired(meta);
  const guestUrl = asset.source?.value;
  const sourceNotes =
    typeof asset.source?.notes === "string" ? asset.source.notes.trim() : "";

  return (
    <div
      className={cn(
        "space-y-2 rounded-lg border border-neutral-200 bg-neutral-50 p-3",
        className
      )}
      data-testid="content-collect-provenance"
    >
      <div className="flex items-center gap-2">
        <i
          className="fa-regular fa-inbox-in text-sm text-neutral-500"
          aria-hidden="true"
        />
        <h4 className="font-gotham text-sm font-semibold text-neutral-900">
          Content Collect
        </h4>
        {modeLabel && (
          <span className="inline-flex h-5 shrink-0 items-center rounded-full border border-neutral-200 bg-white px-2 font-inter text-xs font-medium leading-none text-neutral-600">
            {modeLabel}
          </span>
        )}
        {meta.recorded && (
          <span
            className="inline-flex h-5 shrink-0 items-center gap-1 rounded-full border border-neutral-200 bg-white px-2 font-inter text-xs font-medium leading-none text-neutral-600"
            data-testid="content-collect-recorded"
          >
            <i className="fa-regular fa-circle-dot text-[10px]" />
            Recorded
          </span>
        )}
      </div>

      {meta.collectName && (
        <ProvenanceRow label="Collect">{meta.collectName}</ProvenanceRow>
      )}

      {promptTitle && (
        <ProvenanceRow label="Prompt">{promptTitle}</ProvenanceRow>
      )}

      <ProvenanceRow label="Submitted">
        {submitter ? (
          <>
            {submittedAt ? `${submittedAt} · ` : ""}
            {submitter.name || submitter.email}
            {submitter.name && submitter.email ? ` (${submitter.email})` : ""}
          </>
        ) : (
          <>
            {submittedAt ? `${submittedAt} · ` : ""}
            <span className="text-neutral-500">
              anonymously — this link didn&apos;t ask who was submitting
            </span>
          </>
        )}
      </ProvenanceRow>

      {guestUrl && (
        <ProvenanceRow label="Collect link">
          <ExternalLink
            href={guestUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-blue-500 hover:text-blue-600"
          >
            {guestUrl}
          </ExternalLink>
        </ProvenanceRow>
      )}

      {showSourceNotes && sourceNotes && (
        <p className="font-inter text-xs text-neutral-500">{sourceNotes}</p>
      )}

      <div className="border-t border-neutral-200 pt-2">
        {consent?.accepted ? (
          <div className="space-y-1.5" data-testid="content-collect-consent">
            <p className="font-inter text-sm font-medium text-neutral-700">
              Consent accepted
              {typeof consent.version === "number"
                ? ` · version ${consent.version}`
                : ""}
            </p>

            {permittedUses.length > 0 ? (
              <ul className="space-y-1">
                {permittedUses.map((use) => (
                  <li
                    key={use}
                    className="flex items-center gap-2 font-inter text-sm text-neutral-600"
                  >
                    <i
                      className="fa-regular fa-circle-check text-xs text-green-600"
                      aria-hidden="true"
                    />
                    {humanizePermittedUse(use)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="font-inter text-sm text-neutral-500">
                No specific uses were listed.
              </p>
            )}

            {/* Informational only. Sked records what the contributor agreed to;
                it never restricts the asset, so no copy here may read as a
                block. */}
            {expired && rightsExpiresAt ? (
              <p
                className="flex items-start gap-2 rounded-md border border-yellow-200 bg-yellow-50 px-2 py-1.5 font-inter text-xs text-yellow-700"
                data-testid="content-collect-rights-expired"
              >
                <i
                  className="fa-regular fa-clock-rotate-left mt-0.5 text-[11px]"
                  aria-hidden="true"
                />
                <span>
                  Usage rights expired on {rightsExpiresAt}. This is a record of
                  what the contributor agreed to — check with them before using
                  it again.
                </span>
              </p>
            ) : (
              <p className="font-inter text-sm text-neutral-500">
                {rightsExpiresAt
                  ? `These rights expire on ${rightsExpiresAt}.`
                  : "These rights don't expire."}
              </p>
            )}
          </div>
        ) : (
          <p className="font-inter text-sm text-neutral-500">
            This link didn&apos;t ask for consent, so there&apos;s no record to
            show.
          </p>
        )}
      </div>
    </div>
  );
}
